// src/app/core/http/session-cancel.interceptor.ts
import type { HttpInterceptorFn, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { shareReplay, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../auth/session.service';

/**
 * SessionCancelInterceptor — corta el tráfico HTTP en curso al cerrar sesión
 * (defecto D9, §1.5). Se posiciona antes de RetryInterceptor para que el
 * corte también alcance a los reintentos en espera de su backoff.
 *
 * GET (lecturas, incluidos los reintentos): se cancelan de inmediato al
 * cerrar sesión — su respuesta ya no le sirve a nadie, y cancelar un GET no
 * tiene efectos secundarios en el servidor.
 *
 * POST/PUT/DELETE/PATCH (mutaciones): **nunca** se cancelan a nivel de
 * transporte. Angular aborta el XHR/fetch subyacente al desuscribirse de
 * CUALQUIER método, no solo GET — y una mutación ya pudo haber llegado al
 * servidor y ejecutarse aunque el cliente aborte la conexión antes de leer
 * la respuesta. Abortarla no deshace nada; solo deja al usuario sin saber
 * si su operación se realizó. `shareReplay({ refCount: false })` desacopla
 * la ejecución real de la petición del ciclo de vida de quien la originó:
 * aunque el componente se destruya (navegación a /login) y dejen de
 * escucharla, la petición sigue corriendo hasta completarse en el servidor.
 * `takeUntil(loggedOut$)` solo controla qué ve el llamador original — si la
 * respuesta llega después del cierre, se descarta en silencio para él, sin
 * anunciarse como éxito ni como error (ver también error.interceptor.ts).
 */
export const sessionCancelInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const auth    = inject(AuthService);
  const session = inject(SessionService);

  if (!auth.isAuthenticated()) {
    return next(req);
  }

  if (req.method === 'GET') {
    return next(req).pipe(takeUntil(session.loggedOut$));
  }

  return next(req).pipe(
    shareReplay({ bufferSize: 1, refCount: false }),
    takeUntil(session.loggedOut$),
  );
};
